import { Link } from 'react-router-dom';

const updatedAt = 'May 22, 2026';

export function CookiePolicyPage() {
  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-4xl px-5 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-squishy-primary hover:underline">
          Back to Squishy School
        </Link>

        <header className="mt-6 rounded-2xl border bg-white p-6 shadow-sm sm:p-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-squishy-primary">Cookie Policy</p>
          <h1 className="mt-2 text-3xl font-bold text-squishy-dark sm:text-4xl">Cookie and Local Storage Policy</h1>
          <p className="mt-3 text-sm text-slate-500">Last updated: {updatedAt}</p>
          <p className="mt-5 leading-7 text-slate-700">
            This Cookie and Local Storage Policy explains how Squishy School, operated by SignOut LLC, uses cookies, local storage, and similar technologies when you use our website and web application.
          </p>
        </header>

        <div className="mt-6 space-y-5">
          <Section title="What Are Cookies and Local Storage?">
            <p>
              Cookies are small text files stored on your device by your browser. Local storage is a web browser feature that allows websites to store data on your device. Both are used to remember preferences, maintain sessions, and improve your experience.
            </p>
          </Section>

          <Section title="How We Use These Technologies">
            <p>We use cookies and local storage for the following purposes:</p>
            <ul>
              <li><strong>Essential (strictly necessary):</strong> Authentication tokens, session management, and security tokens required for the Service to function. These cannot be disabled without breaking core functionality.</li>
              <li><strong>Functional:</strong> Remembering your study preferences, display settings, flagged questions, theme choices, and other user preferences to improve your experience.</li>
              <li><strong>Performance and analytics:</strong> Understanding how users interact with the Service, identifying errors, and improving performance. We do not use third-party advertising trackers.</li>
            </ul>
          </Section>

          <Section title="Specific Technologies We Use">
            <ul>
              <li><strong>Authentication tokens:</strong> Stored in local storage to keep you signed in across sessions.</li>
              <li><strong>Study state:</strong> Session progress, flagged questions, and preferences stored locally for a seamless study experience.</li>
              <li><strong>AWS Cognito cookies:</strong> Used for secure authentication with our identity provider.</li>
            </ul>
          </Section>

          <Section title="Third-Party Technologies">
            <p>
              We do not use third-party advertising cookies or cross-site tracking technologies. Our service providers (AWS, analytics tools) may set cookies or similar identifiers for security and performance purposes only.
            </p>
          </Section>

          <Section title="Your Choices">
            <p>
              You can manage cookies through your browser settings. Most browsers allow you to block or delete cookies. However, blocking essential cookies may prevent you from using Squishy School. Clearing local storage will sign you out and reset local preferences.
            </p>
            <p>
              For users in the European Economic Area: we rely on your consent for non-essential cookies and on legitimate interests for essential cookies necessary to provide the Service.
            </p>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              We may update this Cookie Policy from time to time. Changes will be reflected by the updated date above.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              For questions about this Cookie Policy, contact us at <a href="mailto:support@squishyschool.com" className="text-squishy-primary hover:underline">support@squishyschool.com</a>.
            </p>
          </Section>
        </div>
      </div>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border bg-white p-6 shadow-sm">
      <h2 className="text-xl font-bold text-squishy-dark">{title}</h2>
      <div className="privacy-copy mt-3 space-y-3 text-sm leading-7 text-slate-700">
        {children}
      </div>
    </section>
  );
}
